<template>
  <div class="toolbar-wrapper" :class="{ 'collapsed': isCollapsed }">
    <!-- 顶部按钮栏 -->
    <div class="toolbar-header">
      <div class="toolbar-buttons" v-show="!isCollapsed">
        <button class="toolbar-btn" :class="{ 'active': imageType === 'text-to-image' }" @click="imageType = 'text-to-image'">文生图</button>
        <button class="toolbar-btn" :class="{ 'active': imageType === 'image-to-image' }" @click="imageType = 'image-to-image'">图生图</button>
      </div>
      <!-- 收缩/展开按钮 -->
      <button class="collapse-btn" @click="toggleCollapse" :title="isCollapsed ? '展开工具栏' : '收缩工具栏'" :class="{ 'expanded': !isCollapsed }">
        <div class="collapse-arrow" :class="{ 'up': isCollapsed }">▼</div>
      </button>
    </div>
    
    <!-- 提示词输入区 -->
    <div class="prompt-input-container">
      <textarea 
        v-model="form.prompt" 
        class="prompt-input" 
        placeholder="描述您想要生成的图片内容..."
        rows="3"
      ></textarea>
    </div>
    
    <!-- 底部参数选项 -->
    <div class="toolbar-options">
      <!-- 模型选择 -->
      <div class="option-group">
        <select v-model="form.model" class="option-select">
          <option value="" disabled>选择模型</option>
          <optgroup label="免费模型">
            <option v-for="id in freeModels" :key="id" :value="id">{{ models[id]?.name }}</option>
          </optgroup>
          <optgroup label="付费模型">
            <option v-for="id in paidModels" :key="id" :value="id">{{ models[id]?.name }}</option>
          </optgroup>
        </select>
      </div>
      
      <!-- 图片尺寸选择 -->
      <div class="option-group">
        <select v-model="selectedSize" class="option-select">
          <option v-for="size in imageSizes" :key="size.name" :value="size">{{ size.name }}</option>
        </select>
      </div>
      
      <!-- 上传按钮 -->
      <div class="upload-buttons" v-if="imageType === 'image-to-image'">
        <input type="file" ref="imageUpload" accept="image/*" style="display: none" @change="handleImageUpload">
        <button 
          class="upload-btn" 
          @click="openFileUpload"
          title="上传参考图片"
        >
          🖼️
        </button>
        <div v-if="uploadedImagePreview" class="uploaded-preview">
          <img :src="uploadedImagePreview" alt="预览" class="preview-img">
          <button class="remove-preview" @click="removeUploaded">×</button>
        </div>
      </div>
      
      <!-- 相似度进度条 (仅图生图) -->
      <div class="option-group" v-if="imageType === 'image-to-image'">
        <label class="option-label">相似度</label>
        <div class="strength-control">
          <input 
            type="range" 
            v-model="form.strength" 
            min="0.1" 
            max="0.9" 
            step="0.1"
            class="strength-slider"
          >
          <span class="strength-value">{{ form.strength }}</span>
        </div>
      </div>
      
      <!-- 生成按钮 -->
      <button class="generate-btn" @click="generateImage" :disabled="isGenerating || !isModelValid" :title="isModelValid ? '' : '当前模型不支持此功能，请选择其他模型'">
        <span v-if="!isGenerating">生成图片</span>
        <span v-else>生成中...</span>
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import appCache from '../../utils/appCache'

export default {
  name: 'ImageToolbar',
  props: {
    params: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      user_id: 0,
      imageType: 'text-to-image',
      isCollapsed: false,
      isGenerating: false,
      progressInterval: null,
      currentImageIndex: null,
      uploadedFileName: '',
      uploadedImage: null,
      uploadedImagePreview: null,
      models: {},
      freeModels: [],
      paidModels: [],
      imageSizes: [],
      selectedSize: null,
      form: {
        prompt: '',
        model: '',
        size: '',
        strength: 0.5
      },
      hasAutoGenerated: false
    }
  },
  computed: {
    // 检查当前选择的模型是否支持当前imageType的功能
    isModelValid() {
      if (!this.form.model) return false
      const model = this.models[this.form.model]
      if (!model || !model.capabilities) return true
      
      if (this.imageType === 'text-to-image') {
        return model.capabilities.includes('text_to_image')
      } else if (this.imageType === 'image-to-image') {
        return model.capabilities.includes('image_to_image')
      }
      return true
    }
  },
  watch: {
    params: {
      handler(newParams) {
        if (newParams && Object.keys(newParams).length > 0) {
          this.handleParams(newParams)
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
    this.user_id = userInfo.id || 0
    this.init()
  },
  methods: {
    async init() {
      console.log('🚀 ImageToolbar 初始化开始')
      
      await Promise.all([
        this.loadModels(),
        this.loadImageSizes()
      ])
      
      // 从后台配置获取默认模型和分辨率
      try {
        const response = await axios.get('/admin/config/image')
        if (response.data.code === 200) {
          const config = response.data.data
          const defaultModel = config.model || ''
          const defaultSize = config.defaultImageSize || '1024x1024 (1:1)'
          
          // 设置默认模型（只有在没有URL参数传入模型时才设置）
          if (!this.form.model) {
            if (defaultModel && this.models[defaultModel]) {
              this.form.model = defaultModel
            } else if (this.freeModels.length > 0) {
              this.form.model = this.freeModels[0]
            }
          }
          
          // 设置默认分辨率
          if (defaultSize) {
            const matchedSize = this.imageSizes.find(s => s.name === defaultSize)
            if (matchedSize) {
              this.selectedSize = matchedSize
              this.form.size = matchedSize.name
            } else if (this.imageSizes.length > 0 && !this.selectedSize) {
              this.selectedSize = this.imageSizes[0]
              this.form.size = this.imageSizes[0].name
            }
          }
        }
      } catch (error) {
        console.error('获取图片配置失败:', error)
      }
      
      console.log('🚀 ImageToolbar 初始化完成，imageSizes:', this.imageSizes)
    },
    
    async handleParams(params) {
      console.log('=== ImageToolbar 处理参数 ===', params)
      
      // 填充参数
      if (params.type) {
        this.imageType = params.type
      }
      if (params.prompt) {
        this.form.prompt = params.prompt
      }
      if (params.model) {
        this.form.model = params.model
      }
      if (params.size) {
        // 根据参数中的size找到对应的图片尺寸对象
        const matchedSize = this.imageSizes.find(s => s.name === params.size || s.aspect_ratio === params.size)
        if (matchedSize) {
          this.selectedSize = matchedSize
          this.form.size = matchedSize.name
        }
      }
      if (params.strength) {
        this.form.strength = params.strength
      }
      if (params.referenceImage) {
        this.uploadedImagePreview = params.referenceImage
      }
      
      // 等待模型和尺寸加载完成后自动生成
      await this.waitForLoadComplete()
      
      // 避免重复生成
      if (!this.hasAutoGenerated) {
        this.hasAutoGenerated = true
        // 延迟一下，确保所有数据都准备好了
        setTimeout(() => {
          this.generateImage()
        }, 500)
      }
    },
    
    async waitForLoadComplete() {
      // 等待模型和尺寸加载完成
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.imageSizes.length > 0 && Object.keys(this.models).length > 0) {
            clearInterval(checkInterval)
            resolve()
          }
        }, 100)
        // 最多等待3秒
        setTimeout(() => {
          clearInterval(checkInterval)
          resolve()
        }, 3000)
      })
    },
    
    async loadImageSizes() {
      try {
        const sizes = await appCache.getImageSizes()
        if (sizes) {
          this.imageSizes = sizes
          if (this.imageSizes.length > 0 && !this.selectedSize) {
            this.selectedSize = this.imageSizes[0]
            this.form.size = this.imageSizes[0].name
          }
        }
      } catch (error) {
        console.error('加载图片尺寸失败', error)
      }
    },
    async loadModels() {
      try {
        const imageModels = await appCache.getModelsByCategory('image')
        this.models = {}
        this.freeModels = []
        this.paidModels = []
        
        imageModels.forEach(model => {
          this.models[model.identifier] = {
            name: model.name,
            description: model.description,
            type: model.model_type,
            image_url: model.image_url,
            is_new: model.is_new,
            is_hot: model.is_hot,
            discount: model.discount,
            capabilities: model.capabilities || []
          }
          if (model.model_type === 'free') {
            this.freeModels.push(model.identifier)
          } else {
            this.paidModels.push(model.identifier)
          }
        })
        
        // 设置默认模型
        if (!this.form.model && this.freeModels.length > 0) {
          this.form.model = this.freeModels[0]
        }
      } catch (error) {
        console.error('加载模型列表失败', error)
      }
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed
    },
    async generateImage() {
      if (!this.isModelValid) {
        let capabilityName = ''
        if (this.imageType === 'text-to-image') capabilityName = '文生图'
        else if (this.imageType === 'image-to-image') capabilityName = '图生图'
        alert(`当前模型不支持${capabilityName}功能，请选择其他模型`)
        return
      }
      
      if (!this.form.prompt.trim()) {
        alert('请输入提示词')
        return
      }
      
      if (this.imageType === 'image-to-image' && !this.uploadedImagePreview) {
        alert('请先上传参考图片')
        return
      }
      
      console.log('=== 开始generateImage ===')
      console.log('imageType:', this.imageType)
      console.log('uploadedImage:', this.uploadedImage)
      console.log('uploadedImagePreview:', this.uploadedImagePreview ? '有数据' : '无数据')
      
      this.isGenerating = true
      
      // 平滑滚动到顶部，让用户看到生成过程
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
      try {
        let mergedPrompt = this.form.prompt
        
        const selectedWidth = this.selectedSize?.width || 1024
        const selectedHeight = this.selectedSize?.height || 1024
        const aspectRatio = this.selectedSize?.aspect_ratio || '1:1'
        const displayName = this.selectedSize?.name || `${selectedWidth}x${selectedHeight}`
        
        if (displayName) {
          mergedPrompt += `，尺寸：${displayName}`
        }
        
        console.log('mergedPrompt:', mergedPrompt)
        console.log('width:', selectedWidth, 'height:', selectedHeight, 'aspectRatio:', aspectRatio)
        
        const generatingImage = {
          type: this.imageType,
          prompt: this.form.prompt,
          model: this.form.model,
          resolution: displayName,
          strength: this.form.strength,
          status: 'generating',
          referenceImage: this.imageType === 'image-to-image' ? this.uploadedImagePreview : null,
          imageUrl: '',
          progress: 0,
          generateTime: this.getCurrentTime()
        }
        
        this.$emit('add-image', generatingImage)
        this.currentImageIndex = 0
        
        let currentProgress = 0
        this.progressInterval = setInterval(() => {
          if (currentProgress < 90) {
            currentProgress += Math.random() * 10 + 5
            if (currentProgress > 90) currentProgress = 90
            this.$emit('update-progress', this.currentImageIndex, currentProgress)
          }
        }, 500)
        
        let resultImages = []
        
        if (this.imageType === 'text-to-image') {
          // 文生图 - 使用同步接口
          const requestData = {
            model: this.form.model,
            prompt: mergedPrompt,
            width: selectedWidth,
            height: selectedHeight,
            aspect_ratio: aspectRatio,
            count: 1,
            user_id: this.user_id
          }
          
          console.log('调用文生图接口:', requestData)
          
          const response = await axios.post('/image-generate', requestData)
          const data = response.data
          
          if (data.images && data.images.length > 0) {
            resultImages = data.images
          } else {
            throw new Error('生成成功但未返回图片数据')
          }
          
        } else if (this.imageType === 'image-to-image') {
          // 图生图 - 需要先上传图片到存储，再传递URL
          let referenceUrl = null
          
          // 判断 uploadedImagePreview 是存储URL还是base64
          if (this.uploadedImagePreview && (this.uploadedImagePreview.startsWith('/') || this.uploadedImagePreview.startsWith('http'))) {
            // 已经是存储URL（从首页传来）
            referenceUrl = this.uploadedImagePreview
            console.log('使用已上传的参考图片URL:', referenceUrl)
          } else if (this.uploadedImage) {
            // 需要先上传文件到存储
            console.log('图生图：上传参考图片到存储...')
            const uploadForm = new FormData()
            uploadForm.append('image', this.uploadedImage, this.uploadedFileName || 'upload.jpg')
            uploadForm.append('user_id', this.user_id)
            
            const uploadResp = await axios.post('/upload-image', uploadForm, {
              headers: { 'Content-Type': 'multipart/form-data' }
            })
            referenceUrl = uploadResp.data.url
            console.log('参考图片上传成功:', referenceUrl)
          }
          
          if (!referenceUrl) {
            throw new Error('缺少参考图片')
          }
          
          // 调用图生图接口
          const formData = new FormData()
          formData.append('model', this.form.model)
          formData.append('prompt', mergedPrompt)
          formData.append('width', selectedWidth)
          formData.append('height', selectedHeight)
          formData.append('aspect_ratio', aspectRatio)
          formData.append('count', 1)
          formData.append('strength', this.form.strength)
          formData.append('user_id', this.user_id)
          formData.append('reference_image_url', referenceUrl)
          
          console.log('调用图生图接口')
          
          const response = await axios.post('/image-to-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          const data = response.data
          
          if (data.failed) {
            throw new Error(data.message || '生成失败')
          }
          
          if (data.images && data.images.length > 0) {
            resultImages = data.images
          } else {
            throw new Error('生成成功但未返回图片数据')
          }
        }
        
        // 清除进度条
        if (this.progressInterval) {
          clearInterval(this.progressInterval)
          this.progressInterval = null
        }
        
        console.log('图片生成完成:', resultImages)
        this.$emit('update-result', this.currentImageIndex, resultImages[0])
        
      } catch (error) {
        console.error('生成失败:', error)
        if (this.progressInterval) {
          clearInterval(this.progressInterval)
          this.progressInterval = null
        }
        if (this.currentImageIndex !== null) {
          this.$emit('remove-image', this.currentImageIndex)
        }
      } finally {
        this.isGenerating = false
        this.currentImageIndex = null
      }
    },
    
    getCurrentTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    openFileUpload() {
      this.$refs.imageUpload.click()
    },
    retryFromItem(item) {
      console.log('=== ImageToolbar: retryFromItem ===')
      console.log('item:', item)
      
      this.form.prompt = item.prompt || ''
      this.form.model = item.model || ''
      this.form.strength = item.strength || 0.5
      this.imageType = item.type || 'text-to-image'
      
      if (item.referenceImage) {
        this.uploadedImagePreview = item.referenceImage
        this.$nextTick(() => {
          if (this.$refs.uploadedPreview) {
            this.$refs.uploadedPreview.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
      }
      
      alert('参数已填充，请点击生成按钮重新开始生成')
    },
    handleImageUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.uploadedFileName = file.name
        this.uploadedImage = file
        // 生成预览
        const reader = new FileReader()
        reader.onload = (e) => {
          this.uploadedImagePreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    removeUploaded() {
      this.uploadedImage = null
      this.uploadedFileName = ''
      this.uploadedImagePreview = null
    }
  }
}
</script>

<style scoped>
.toolbar-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  transition: all 0.3s ease;
  overflow: visible;
  z-index: 100;
}

.toolbar-wrapper.collapsed {
  gap: 0;
}

/* 工具栏头部 */
.toolbar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
}

.toolbar-buttons {
  display: flex;
  gap: 12px;
  flex: 1;
  transition: all 0.3s ease;
}

.toolbar-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #e6e6e6;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.toolbar-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(0, 230, 172, 0.3);
  color: #00e6ac;
}

.toolbar-btn.active {
  background: #00e6ac;
  border-color: #00e6ac;
  color: #000;
  font-weight: 600;
}

/* 收缩/展开按钮 */
.collapse-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 0;
  z-index: 10;
}

.collapse-arrow {
  font-size: 24px;
  color: #ff4757;
  transition: all 0.3s ease;
  line-height: 1;
}

.collapse-arrow.up {
  color: #2ed573;
  transform: rotate(180deg);
}

.toolbar-wrapper.collapsed .collapse-btn {
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px;
}

.toolbar-wrapper.collapsed .collapse-arrow {
  color: #2ed573;
}

/* 提示词输入区 */
.prompt-input-container {
  width: 100%;
  transition: all 0.3s ease;
}

.toolbar-wrapper.collapsed .prompt-input-container {
  opacity: 0;
  height: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.prompt-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  color: #e6e6e6;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.prompt-input:focus {
  outline: none;
  border-color: rgba(0, 230, 172, 0.5);
  background: rgba(255, 255, 255, 0.08);
}

.prompt-input::placeholder {
  color: #666;
}

/* 底部参数选项 */
.toolbar-options {
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
}

.toolbar-wrapper.collapsed .toolbar-options {
  opacity: 0;
  height: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.option-select {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px 12px;
  color: #e6e6e6;
  font-size: 14px;
  min-width: 120px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.option-select:focus {
  outline: none;
  border-color: rgba(0, 230, 172, 0.5);
}

.option-select option {
  background: #2a2a2a;
  color: #e6e6e6;
}

.option-select optgroup {
  background: #1a1a1a;
  color: #00e6ac;
  font-weight: 600;
}

.option-label {
  color: #e6e6e6;
  font-size: 14px;
  white-space: nowrap;
}

.strength-control {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 150px;
}

.strength-slider {
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}

.strength-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: #00e6ac;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
}

.strength-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 0 0 8px rgba(0, 230, 172, 0.1);
}

.strength-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: #00e6ac;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
}

.strength-slider::-moz-range-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 0 0 8px rgba(0, 230, 172, 0.1);
}

.strength-value {
  color: #00e6ac;
  font-size: 14px;
  font-weight: 600;
  min-width: 40px;
  text-align: center;
}

/* 生成按钮 */
.generate-btn {
  background: #00e6ac;
  color: #000;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: auto;
  white-space: nowrap;
}

.generate-btn:hover {
  background: #00d09d;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 230, 172, 0.3);
}

.generate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.upload-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.upload-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #e6e6e6;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  box-sizing: border-box;
}

.upload-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(0, 230, 172, 0.3);
  color: #00e6ac;
}

.upload-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.uploaded-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.preview-img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid rgba(0, 230, 172, 0.3);
  background: rgba(255, 255, 255, 0.02);
}

.remove-preview {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 71, 87, 0.9);
  color: white;
  border: none;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  line-height: 1;
  padding: 0;
}

.remove-preview:hover {
  background: rgba(255, 71, 87, 1);
  transform: scale(1.1);
}

.uploaded-file-name {
  color: #00e6ac;
  font-size: 14px;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.upload-hint {
  color: #666;
  font-size: 12px;
  white-space: nowrap;
}

.toolbar-wrapper.collapsed .upload-section {
  opacity: 0;
  height: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

/* 进度条样式 */
.progress-container {
  width: 100%;
  padding: 8px 0;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00e6ac, #00d09d);
  border-radius: 2px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
}

.progress-wave {
  position: absolute;
  top: 0;
  left: 0;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: wave 1s infinite linear;
}

@keyframes wave {
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0);
  }
}

@media (max-width: 768px) {
  .toolbar-options {
    flex-direction: column;
    align-items: stretch;
  }
  
  .generate-btn {
    margin-left: 0;
    width: 100%;
  }
  
  .upload-section {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .upload-btn {
    width: 100%;
  }
  
  .uploaded-file-name {
    white-space: normal;
  }
  
  .upload-hint {
    text-align: center;
  }
}

/* 模型选择器样式 */
.model-select-wrapper {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.model-select-container {
  position: relative;
  width: 100%;
}

.selected-model {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 36px;
  min-width: 180px;
  box-sizing: border-box;
}

.selected-model:hover {
  border-color: #00e6ac;
  background: rgba(0, 230, 172, 0.05);
}

.model-icon {
  width: 24px;
  height: 24px;
  margin-right: 10px;
  border-radius: 4px;
  object-fit: cover;
}
</style>